import { useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router";

import { CategoryChips, WatchCategoriesDialog } from "../components/Categories";
import { ConfirmDialog, RenameDialog } from "../components/Dialog";
import { ErrorState, NotFound } from "../components/ErrorState";
import { EventTimeline } from "../components/EventTimeline";
import { Loading } from "../components/Loading";
import { SituationStamp } from "../components/Stamp";
import { useToast } from "../components/Toast";
import {
  ApiError,
  downloadFile,
  type CompanyDocument,
  type CompanyEvent,
  type FinanceYear,
  type MarketQuote,
  type RegistryFact,
  type SourceStatus,
} from "../lib/api";
import { formatAmount, formatDate, formatPercent, formatPrice, formatRelative, plural } from "../lib/format";
import { useCompany, useRemoveWatch, useUpdateWatch } from "../lib/queries";
import { formatSiren } from "../lib/siren";
import { useTitle } from "../lib/useTitle";

type Category = "all" | "procedures" | "company" | "accounts" | "market";

const PROCEDURES = new Set([
  "liquidation",
  "redressement",
  "sauvegarde",
  "procedure_collective",
  "conciliation",
  "retablissement_professionnel",
  "jugement",
]);

const ACCOUNTS = new Set(["depot_comptes", "finances"]);
const MARKET = "bourse";

const CATEGORIES: { key: Category; label: string; match: (e: CompanyEvent) => boolean }[] = [
  { key: "all", label: "Tout", match: () => true },
  { key: "procedures", label: "Procédures", match: (e) => PROCEDURES.has(e.kind) },
  {
    key: "company",
    label: "Vie de l'entreprise",
    match: (e) => !PROCEDURES.has(e.kind) && !ACCOUNTS.has(e.kind) && e.kind !== MARKET,
  },
  { key: "accounts", label: "Comptes", match: (e) => ACCOUNTS.has(e.kind) },
  { key: "market", label: "Bourse", match: (e) => e.kind === MARKET },
];

const SOURCE_NAMES: Record<string, string> = {
  bodacc: "BODACC",
  registre: "Répertoire des entreprises",
  rne: "INPI – RNE",
  bourse: "Bourse",
  judilibre: "Judilibre",
};

export function Company() {
  const { siren = "" } = useParams();
  const company = useCompany(siren);
  const navigate = useNavigate();
  const toast = useToast();
  const [category, setCategory] = useState<Category>("all");
  const [withHistory, setWithHistory] = useState(true);
  const [renaming, setRenaming] = useState(false);
  const [removing, setRemoving] = useState(false);
  const rename = useUpdateWatch(siren);
  const [categorizing, setCategorizing] = useState(false);
  const remove = useRemoveWatch();

  const data = company.data;
  const title = data ? (data.watch.label ?? data.name ?? formatSiren(siren)) : null;
  useTitle(title);

  const events = useMemo(() => {
    const match = CATEGORIES.find((c) => c.key === category)!.match;
    return (data?.events ?? []).filter((e) => match(e) && (withHistory || !e.is_history));
  }, [data, category, withHistory]);

  if (company.isPending) {
    return (
      <div className="page">
        <Loading label="Chargement de la fiche" />
      </div>
    );
  }
  if (company.isError) {
    if (company.error instanceof ApiError && [404, 422].includes(company.error.status)) return <NotFound />;
    return <ErrorState error={company.error} onRetry={() => company.refetch()} />;
  }
  if (!data || !title) return null;

  const syncing = !data.watch.last_checked_at;
  const historyCount = data.events.filter((e) => e.is_history).length;

  return (
    <div className="page">
      <Link className="back-link" to="/tableau-de-bord">
        Retour à mes entreprises
      </Link>

      <header className="company-head">
        <h1>{title}</h1>
        {data.watch.label && data.name && <div className="company-head__label">{data.name}</div>}
        <div>
          <SituationStamp situation={data.situation} size="large" showSince />
        </div>
        <div className="company-head__meta">
          <span>SIREN {formatSiren(data.siren)}</span>
          {data.watch.city && <span>{data.watch.city}</span>}
          <span>Suivie depuis le {formatDate(data.watch.created_at)}</span>
        </div>
        <CategoryChips categories={data.watch.categories} />
        <div className="actions">
          <button type="button" className="button button--quiet button--small" onClick={() => setCategorizing(true)}>
            {data.watch.categories.length ? "Modifier les catégories" : "Ranger dans une catégorie"}
          </button>
          <button type="button" className="button button--quiet button--small" onClick={() => setRenaming(true)}>
            Renommer pour moi
          </button>
          <button type="button" className="button button--quiet button--small" onClick={() => setRemoving(true)}>
            Arrêter le suivi
          </button>
          <a className="button button--link" href={data.public_url} target="_blank" rel="noopener noreferrer">
            Fiche publique sur l'Annuaire des entreprises
          </a>
        </div>
        <OfficialDocuments siren={data.siren} avisUrl={data.avis_situation_url} inpiUrl={data.inpi_extract_url} />
      </header>

      <div className="company-layout">
        <section aria-labelledby="timeline-title">
          <div className="timeline-head">
            <h2 id="timeline-title">Chronologie</h2>
            <div className="segmented" role="group" aria-label="Filtrer les annonces">
              {CATEGORIES.map((c) => (
                <button key={c.key} type="button" aria-pressed={category === c.key} onClick={() => setCategory(c.key)}>
                  {c.label}
                </button>
              ))}
            </div>
          </div>

          {syncing && (
            <div className="notice first-sync" role="status">
              <Loading label="Première lecture du BODACC en cours. La chronologie se complète automatiquement." />
            </div>
          )}

          {historyCount > 0 && (
            <p className="history-toggle">
              <label>
                <input type="checkbox" checked={withHistory} onChange={(e) => setWithHistory(e.target.checked)} />{" "}
                Afficher les {plural(historyCount, "annonce antérieure", "annonces antérieures")} au début du suivi
              </label>
            </p>
          )}

          {events.length > 0 ? (
            <EventTimeline events={events} />
          ) : (
            !syncing && (
              <div className="empty">
                <h2>Aucune annonce ici</h2>
                <p>
                  {data.events.length
                    ? "Aucune annonce ne correspond à ce filtre."
                    : "Rien n'a été publié pour cette entreprise. Vous serez prévenu dès la première annonce."}
                </p>
              </div>
            )
          )}
        </section>

        <aside className="aside" aria-label="Informations sur l'entreprise">
          {data.finances.length > 0 && <KeyFigures finances={data.finances} />}
          {data.market && <MarketPanel market={data.market} />}
          <section>
            <h2>Au répertoire</h2>
            {data.registry.length ? <Facts facts={data.registry} /> : <p className="field__hint">Pas encore lu.</p>}
          </section>
          {data.documents.length > 0 && <DocumentList documents={data.documents} />}
          <section>
            <h2>Dernières vérifications</h2>
            <SourceList sources={data.sources} />
          </section>
        </aside>
      </div>

      <WatchCategoriesDialog watch={data.watch} open={categorizing} onClose={() => setCategorizing(false)} />
      <RenameDialog
        open={renaming}
        onClose={() => setRenaming(false)}
        initial={data.watch.label ?? ""}
        companyName={data.name ?? data.siren}
        pending={rename.isPending}
        onSave={(label) =>
          rename.mutate(
            { id: data.watch.id, label },
            {
              onSuccess: () => {
                setRenaming(false);
                toast("Nom enregistré.");
              },
              onError: (error) => toast(error.message, "error"),
            },
          )
        }
      />
      <ConfirmDialog
        open={removing}
        onClose={() => setRemoving(false)}
        title={`Arrêter le suivi de ${title} ?`}
        body="Vous ne recevrez plus d'alerte pour cette entreprise. Vous pourrez la rajouter plus tard."
        confirmLabel="Arrêter le suivi"
        danger
        pending={remove.isPending}
        onConfirm={() =>
          remove.mutate(
            { id: data.watch.id, siren: data.siren },
            {
              onSuccess: () => {
                toast(`Suivi de ${title} arrêté.`);
                navigate("/tableau-de-bord");
              },
              onError: (error) => toast(error.message, "error"),
            },
          )
        }
      />
    </div>
  );
}

function Facts({ facts }: { facts: RegistryFact[] }) {
  return (
    <dl className="facts">
      {facts.map((fact) => (
        <div key={fact.key}>
          <dt>{fact.label}</dt>
          <dd>
            {Array.isArray(fact.value) ? (
              fact.value.length ? (
                <ul>
                  {fact.value.map((v) => (
                    <li key={v}>{v}</li>
                  ))}
                </ul>
              ) : (
                "Aucun"
              )
            ) : fact.value ? (
              /^\d{4}-\d{2}-\d{2}$/.test(fact.value) ? formatDate(fact.value) : fact.value
            ) : (
              "Non renseigné"
            )}
          </dd>
        </div>
      ))}
    </dl>
  );
}

function SourceList({ sources }: { sources: SourceStatus[] }) {
  if (!sources.length) return <p className="field__hint">Aucune vérification pour l'instant.</p>;
  return (
    <ul className="source-status">
      {sources.map((s) => {
        const failing =
          Boolean(s.last_error) &&
          (!s.last_success_at || Date.parse(s.last_error_at ?? "") > Date.parse(s.last_success_at));
        return (
          <li key={s.source}>
            <span>{SOURCE_NAMES[s.source] ?? s.source}</span>
            <span className={failing ? "is-error" : undefined}>
              {failing ? "Échec, nouvel essai demain" : formatRelative(s.last_success_at)}
            </span>
          </li>
        );
      })}
    </ul>
  );
}

function growth(current: number | null, previous: number | null | undefined): number | null {
  if (current === null || previous === null || previous === undefined || previous === 0) return null;
  return (current - previous) / Math.abs(previous);
}

function trendClass(value: number | null): string | undefined {
  if (value === null || value === 0) return undefined;
  return value > 0 ? "is-up" : "is-down";
}

function KeyFigures({ finances }: { finances: FinanceYear[] }) {
  return (
    <section aria-labelledby="key-figures-title">
      <h2 id="key-figures-title">Chiffres clés</h2>
      <table className="key-figures">
        <thead>
          <tr>
            <th scope="col">Exercice</th>
            <th scope="col">Chiffre d'affaires</th>
            <th scope="col">Résultat net</th>
            <th scope="col">
              <abbr title="Évolution du chiffre d'affaires par rapport à l'exercice précédent">Évol. CA</abbr>
            </th>
          </tr>
        </thead>
        <tbody>
          {finances.map((f, i) => {
            const change = growth(f.ca, finances[i + 1]?.ca);
            return (
              <tr key={f.year}>
                <th scope="row">{f.year}</th>
                <td className="num">{formatAmount(f.ca)}</td>
                <td className={f.resultat_net !== null && f.resultat_net < 0 ? "num is-negative" : "num"}>
                  {formatAmount(f.resultat_net)}
                </td>
                <td className={["num", trendClass(change)].filter(Boolean).join(" ")}>{formatPercent(change)}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </section>
  );
}

function Sparkline({ series, currency }: { series: [string, number][]; currency: string | null }) {
  const points = [...series].sort((a, b) => a[0].localeCompare(b[0]));
  const first = points[0];
  const last = points[points.length - 1];
  if (!first || !last || points.length < 2) return null;

  const width = 120;
  const height = 32;
  const values = points.map(([, v]) => v);
  const min = Math.min(...values);
  const span = Math.max(...values) - min || 1;
  const path = points
    .map(([, v], i) => {
      const x = (i / (points.length - 1)) * width;
      const y = height - 2 - ((v - min) / span) * (height - 4);
      return `${x.toFixed(1)},${y.toFixed(1)}`;
    })
    .join(" ");
  const change = growth(last[1], first[1]);
  const label =
    `Cours du ${formatDate(first[0])} au ${formatDate(last[0])} : ` +
    `de ${formatPrice(first[1], currency)} à ${formatPrice(last[1], currency)}` +
    (change === null ? "" : ` (${formatPercent(change)})`);

  return (
    <svg
      className={["sparkline", trendClass(change)].filter(Boolean).join(" ")}
      role="img"
      aria-label={label}
      viewBox={`0 0 ${width} ${height}`}
      preserveAspectRatio="none"
    >
      <polyline points={path} fill="none" stroke="currentColor" strokeWidth="1.5" vectorEffect="non-scaling-stroke" />
    </svg>
  );
}

function MarketPanel({ market }: { market: MarketQuote }) {
  return (
    <section aria-labelledby="market-title" className="market">
      <h2 id="market-title">Bourse</h2>
      <div className="market__quote">
        <span className="market__symbol">{market.symbol}</span>
        <span className="market__price">{formatPrice(market.last_close, market.currency)}</span>
        {market.change_pct !== null && (
          <span className={["market__change", trendClass(market.change_pct)].filter(Boolean).join(" ")}>
            {formatPercent(market.change_pct)}
            <span className="visually-hidden"> sur la séance</span>
          </span>
        )}
      </div>
      <p className="field__hint">Clôture au {formatDate(market.as_of)}</p>
      <Sparkline series={market.series} currency={market.currency} />
      <a className="button button--link" href={market.url} target="_blank" rel="noopener noreferrer">
        Voir le cours
      </a>
      <p className="market__note">Données indicatives</p>
    </section>
  );
}

const DOCUMENT_TYPES: Record<CompanyDocument["type"], { label: string; tone: string }> = {
  acte: { label: "Acte", tone: "medium" },
  bilan: { label: "Comptes", tone: "low" },
};

function DocumentList({ documents }: { documents: CompanyDocument[] }) {
  return (
    <section aria-labelledby="documents-title">
      <h2 id="documents-title">Documents déposés</h2>
      <ul className="documents">
        {documents.map((doc) => {
          const type = DOCUMENT_TYPES[doc.type] ?? { label: doc.type, tone: "low" };
          return (
            <li key={`${doc.type}-${doc.id}`}>
              <div className="documents__head">
                <span className={`tag tag--${type.tone}`}>{type.label}</span>
                <time dateTime={doc.date ?? undefined}>{formatDate(doc.date, "short")}</time>
              </div>
              <div className="documents__label">{doc.label}</div>
              {doc.download_url ? (
                <a className="documents__action" href={doc.download_url} download>
                  Télécharger le PDF<span className="visually-hidden"> : {doc.label}</span>
                </a>
              ) : (
                <span className="documents__action documents__action--muted">Confidentiel</span>
              )}
            </li>
          );
        })}
      </ul>
    </section>
  );
}

function OfficialDocuments({ siren, avisUrl, inpiUrl }: { siren: string; avisUrl: string | null; inpiUrl: string }) {
  const toast = useToast();
  const [loading, setLoading] = useState(false);

  async function downloadAvis() {
    if (!avisUrl) return;
    setLoading(true);
    try {
      await downloadFile(avisUrl, `avis-situation-sirene-${siren}.pdf`);
    } catch (error) {
      toast(error instanceof Error ? error.message : "Téléchargement impossible.", "error");
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="official-docs" role="group" aria-label="Documents officiels à télécharger">
      {avisUrl && (
        <button type="button" className="button button--quiet button--small" onClick={downloadAvis} disabled={loading}>
          <DownloadIcon />
          {loading ? "Téléchargement en cours" : "Avis de situation Sirene"}
        </button>
      )}
      <a
        className="button button--quiet button--small"
        href={inpiUrl}
        target="_blank"
        rel="noopener noreferrer"
        aria-describedby="inpi-hint"
      >
        <DownloadIcon />
        Extrait RNE de l'INPI
      </a>
      <span id="inpi-hint" className="field__hint">
        PDF officiels. L'avis de l'INSEE est généré le jour même ; l'extrait s'ouvre sur data.inpi.fr.
      </span>
    </div>
  );
}

function DownloadIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 16 16" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="1.8">
      <path d="M8 2v8M4.5 6.5 8 10l3.5-3.5M2.5 13.5h11" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}
